import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  /** Width class applied to the panel. Default: max-w-3xl. */
  widthClass?: string;
}

/**
 * Right-anchored overlay panel. Clicking the backdrop or pressing Escape closes
 * the sheet. Content keeps the underlying page visible — useful when the
 * background also updates live (e.g. review list polling).
 */
export function Sheet({ open, onClose, title, children, widthClass = 'max-w-3xl' }: SheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <div
      data-testid="sheet-root"
      aria-hidden={!open}
      className={`fixed inset-0 z-40 transition-opacity duration-200 ${
        open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
    >
      <button
        type="button"
        aria-label="Close"
        data-testid="sheet-backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 cursor-default"
      />
      <aside
        role="dialog"
        aria-modal="true"
        data-testid="sheet-panel"
        className={`absolute right-0 top-0 h-full w-full ${widthClass} bg-gray-900 border-l border-gray-700 shadow-2xl flex flex-col transition-transform duration-200 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-gray-700 shrink-0">
          <div className="text-sm font-medium text-gray-200">{title}</div>
          <button
            type="button"
            onClick={onClose}
            data-testid="sheet-close"
            className="p-1 rounded hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
            aria-label="Close panel"
          >
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </aside>
    </div>
  );
}
